import {
  Alert,
  Button,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { activateCodelist, getCodelistDiff } from '../../api/endpoints';
import { queryKeys } from '../../api/queryKeys';
import type { CodelistEntry, CodelistVersion } from '../../api/types';
import { ConfirmDialog } from '../../components/ConfirmDialog';
import { ErrorBlock, LoadingBlock } from '../../components/QueryStateOverlay';
import { useErrorToast, useSuccessToast } from '../../hooks/useErrorToast';
import { useSelectedTenant } from '../../tenant/tenantSelection';
import { kindLabelKeys } from './codelistLabels';

export interface CodelistDiffDialogProps {
  /** A draft — the server refuses a diff of anything else. */
  version: CodelistVersion;
  onClose: () => void;
}

function EntryChips({ entries, color }: { entries: CodelistEntry[]; color: 'success' | 'error' }) {
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {entries.map((entry) => (
        <Tooltip key={entry.code} title={entry.description ?? ''}>
          <Chip size="small" variant="outlined" color={color} label={`${entry.code} — ${entry.name}`} />
        </Tooltip>
      ))}
    </Stack>
  );
}

/**
 * What activating a draft would change: the codes it adds, the ones it deprecates, and the
 * tenant's enabled mappings that would end up outside the list. Activation is confirmed here with
 * that count in front of the user, since the server never rewrites those mappings for them.
 */
export function CodelistDiffDialog({ version, onClose }: CodelistDiffDialogProps) {
  const queryClient = useQueryClient();
  const toastError = useErrorToast();
  const toastSuccess = useSuccessToast();
  const { t } = useTranslation();
  const { selectedTenantGid } = useSelectedTenant();

  const [confirming, setConfirming] = useState(false);

  // Keyed by the selected tenant: the diff itself is shared, the affected mappings it lists are not.
  const query = useQuery({
    queryKey: queryKeys.codelists.diff(selectedTenantGid, version.versionGid),
    queryFn: ({ signal }) => getCodelistDiff(version.versionGid, signal),
  });

  const mutation = useMutation({
    mutationFn: () => activateCodelist(version.versionGid),
    onSuccess: async (result) => {
      // The reader the mapping dialog uses and the "not in code list" flag on the mappings both
      // change with the active version, not only the version listing.
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.codelists.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.mappings.all }),
      ]);
      toastSuccess(t('codelists.versions.activated', { count: result.brokenMappingCount }));
      setConfirming(false);
      onClose();
    },
    onError: (error) => {
      toastError(error);
      setConfirming(false);
    },
  });

  const diff = query.data;
  const affectedCount = diff?.affectedMappings.length ?? 0;

  return (
    <>
      <Dialog open onClose={mutation.isPending ? undefined : onClose} maxWidth="md" fullWidth>
        <DialogTitle>{t('codelists.versions.diff.title', { version: version.versionLabel })}</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t(kindLabelKeys[version.kind])}
          </Typography>

          {query.isPending && <LoadingBlock />}
          {query.isError && <ErrorBlock error={query.error} onRetry={() => void query.refetch()} />}

          {diff && (
            <Stack spacing={2.5}>
              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  {t('codelists.versions.diff.added')} ({diff.added.length})
                </Typography>
                {diff.added.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                ) : (
                  <EntryChips entries={diff.added} color="success" />
                )}
              </Stack>

              <Stack spacing={1}>
                <Typography variant="subtitle2">
                  {t('codelists.versions.diff.deprecated')} ({diff.deprecated.length})
                </Typography>
                {diff.deprecated.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">
                    —
                  </Typography>
                ) : (
                  <EntryChips entries={diff.deprecated} color="error" />
                )}
              </Stack>

              <Typography variant="body2" color="text.secondary">
                {t('codelists.versions.diff.unchanged', { count: diff.unchangedCount })}
              </Typography>

              <Stack spacing={1}>
                <Typography variant="subtitle2">{t('codelists.versions.diff.affected')}</Typography>
                {diff.affectedMappings.length === 0 ? (
                  <Alert severity="success">{t('codelists.versions.diff.noAffected')}</Alert>
                ) : (
                  <TableContainer sx={{ maxHeight: 320 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell>{t('columns.tenant')}</TableCell>
                          <TableCell>{t('mappings.columns.sourceValue')}</TableCell>
                          <TableCell>{t('mappings.columns.targetValue')}</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {diff.affectedMappings.map((mapping) => (
                          <TableRow key={mapping.mappingGid} hover>
                            <TableCell>{mapping.tenantName}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{mapping.sourceValue}</TableCell>
                            <TableCell sx={{ fontFamily: 'monospace' }}>{mapping.targetValue}</TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </Stack>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={mutation.isPending}>
            {t('actions.close')}
          </Button>
          <Button
            variant="contained"
            color={affectedCount > 0 ? 'error' : 'primary'}
            onClick={() => setConfirming(true)}
            disabled={!diff || mutation.isPending}
          >
            {t('codelists.versions.diff.activate')}
          </Button>
        </DialogActions>
      </Dialog>

      <ConfirmDialog
        open={confirming}
        title={t('codelists.versions.diff.activate')}
        message={t('codelists.versions.diff.confirmActivate', {
          version: version.versionLabel,
          count: affectedCount,
        })}
        confirmLabel={t('codelists.versions.diff.activate')}
        confirmColor={affectedCount > 0 ? 'error' : 'primary'}
        busy={mutation.isPending}
        onConfirm={() => mutation.mutate()}
        onClose={() => setConfirming(false)}
      />
    </>
  );
}
